// Pure data-shaping helpers: group a document's flat entity / relationship
// lists into per-label / per-type accordion groups.

import type { DocumentEntity, DocumentRelationship } from '@/services/api/cases'

// `total` is the exact server-side count for the group; `entities` /
// `relationships` hold a bounded sample, so `total` can exceed the list length
// (the accordion then says how many rows were not shipped).
export type EntityGroup = { label: string; entities: DocumentEntity[]; total: number }
export type RelationshipGroup = {
  type: string
  relationships: DocumentRelationship[]
  total: number
}

export function groupEntitiesByLabel(
  entities: DocumentEntity[],
  labelCounts?: Record<string, number>
): EntityGroup[] {
  const map = new Map<string, DocumentEntity[]>()
  for (const e of entities) {
    const list = map.get(e.label) ?? []
    list.push(e)
    map.set(e.label, list)
  }
  // A label can exist only in the counts (its whole sample was elided) — pull
  // those in too so the panel never hides a label the way the old cap did.
  for (const label of Object.keys(labelCounts ?? {})) {
    if (!map.has(label)) map.set(label, [])
  }
  return Array.from(map.entries()).map(([label, list]) => ({
    label,
    entities: list,
    total: labelCounts?.[label] ?? list.length,
  }))
}

export function groupRelationshipsByType(
  relationships: DocumentRelationship[],
  typeCounts?: Record<string, number>
): RelationshipGroup[] {
  const map = new Map<string, DocumentRelationship[]>()
  for (const r of relationships) {
    const list = map.get(r.type) ?? []
    list.push(r)
    map.set(r.type, list)
  }
  for (const type of Object.keys(typeCounts ?? {})) {
    if (!map.has(type)) map.set(type, [])
  }
  return Array.from(map.entries()).map(([type, list]) => ({
    type,
    relationships: list,
    total: typeCounts?.[type] ?? list.length,
  }))
}

export interface ChunkSummary {
  id: string
  text: string
  chunkIndex: number | null
  chunkType: string | null
  section: string | null
}

interface InternalNode {
  id: string
  label: string
  properties: Record<string, unknown>
}

/** Extract + sort `Chunk` nodes from the internal-provenance node list. */
export function chunksFromInternalNodes(nodes: InternalNode[]): ChunkSummary[] {
  return nodes
    .filter(n => n.label === 'Chunk')
    .map(n => ({
      id: n.id,
      text: typeof n.properties.text === 'string' ? n.properties.text : '',
      chunkIndex: typeof n.properties.chunk_index === 'number' ? n.properties.chunk_index : null,
      chunkType: typeof n.properties.chunk_type === 'string' ? n.properties.chunk_type : null,
      section: typeof n.properties.section === 'string' ? n.properties.section : null,
    }))
    .sort((a, b) => (a.chunkIndex ?? 0) - (b.chunkIndex ?? 0))
}
